import "./Table.scss";

import { Tooltip } from "../Tooltip";
import { IconDelete, IconSortAsc, IconSortDesc, IconUpdate } from "../Icons";
import { LoadingSpinner } from "../Loading";
import { useState } from "react";
import { orderBy } from "lodash-es";
import { Pill } from "../Pill";

interface Column {
  key: string;
  text: string;
  align?: string;
  width?: string;
}

interface Props {
  columns: Column[];
  rows?: any;
  onActionUpdate?: any;
  onActionDelete?: any;
  onClickRow?: any;
  loading?: boolean;
  selectedRowIndex?: number | null;
}

export const Table = ({
  columns,
  rows,
  onActionUpdate,
  onActionDelete,
  onClickRow,
  loading,
  selectedRowIndex,
}: Props) => {
  const [orderByColumn, setOrderByColumn] = useState<string>("");
  const [orderByAsc, setOrderByAsc] = useState<"asc" | "desc">("asc");

  const orderedRows =
    orderByColumn && rows ? orderBy(rows, [orderByColumn], [orderByAsc]) : rows;

  const getAlignment = (column: Column) => {
    if (column.align) return column.align;
    return "";
  };

  const isColumnSortable = (column: Column) => column.key !== "actions";

  const onClickTableRow = (row: any, i: number) => {
    if (onClickRow) {
      onClickRow(row, i);
    }
  };

  const onClickTableColumn = (column: Column) => {
    if (!isColumnSortable(column)) return;

    if (orderByColumn !== column.key) {
      setOrderByColumn(column.key);
      setOrderByAsc("asc");
    } else {
      setOrderByAsc(orderByAsc === "asc" ? "desc" : "asc");
    }
  };

  const onClickUpdate = (row: any) => {
    if (onActionUpdate) {
      onActionUpdate(row);
    }
  };

  const onClickDelete = (row: any) => {
    if (onActionDelete) {
      onActionDelete(row);
    }
  };

  const getSortIcon = (column: Column) => {
    if (orderByColumn !== column.key) return;
    return orderByAsc === "asc" ? <IconSortAsc /> : <IconSortDesc />;
  };

  const getActions = (row: any) => (
    <div className="action-icons">
      <Tooltip text="Actualizar">
        <IconUpdate onClick={() => onClickUpdate(row)} />
      </Tooltip>
      <Tooltip text="Eliminar">
        <IconDelete onClick={() => onClickDelete(row)} />
      </Tooltip>
    </div>
  );

  const getStatus = (row: any) => <Pill status={row.id_status}></Pill>;

  const renderCell = (column: any, row: any) => {
    if (column.key === "actions") return getActions(row);
    if (column.key === "id_status") return getStatus(row);
    return row[column.key] ?? "";
  };

  return loading ? (
    <LoadingSpinner />
  ) : (
    <div className="table">
      <div className="header">
        <div className="row">
          {columns.map((column, index) => (
            <div
              key={`col-${index}`}
              onClick={() => onClickTableColumn(column)}
              className={`cell flex-row ${getAlignment(column)} ${
                isColumnSortable(column) ? "pointer" : ""
              }`}
              style={{ width: column.width ? column.width : "100%" }}
            >
              {column.align === "right" && getSortIcon(column)}
              {column.text}
              {column.align !== "right" && getSortIcon(column)}
            </div>
          ))}
        </div>
      </div>
      <div className="body">
        {(!orderedRows || orderedRows.length === 0) && (
          <div className="row empty">
            <div className="cell center">Sin datos</div>
          </div>
        )}

        {orderedRows &&
          orderedRows.map((row: any, i: number) => (
            <div
              key={`row-${i}`}
              className={`row ${selectedRowIndex === i ? 'row-selected' : ''}`}
              onClick={() => onClickTableRow(row, i)}
              style={{ cursor: onClickRow ? "pointer" : "auto" }}
            >
              {columns.map((column, colIndex) => (
                <div
                  key={`${i}-${colIndex}`}
                  className={`cell ${getAlignment(column)}`}
                  style={{ width: column.width ? column.width : "100%" }}
                >
                  {renderCell(column, row)}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};
